import { useTranslation } from "react-i18next";
import { CustomModal } from "../../../components/CustomModal/CustomModal";
import { CustomTextInput } from "../../../components/CustomTextInput/CustomTextInput";
import useStyles from "./styles";
import { FC, useEffect, useState } from "react";
import { CustomDropZoneUpload } from "../../../components/CustomDropZoneUpload/CustomDropZoneUpload";
import { CustomCheckboxSquare } from "../../../components/CustomCheckboxSquare/CustomCheckboxSquare";
import { useCreateCompanie, useUpdateCompanie } from "../../../api/companies";
import { uploadToStorage } from "../../../api/media";
import toast from "react-hot-toast";

type props = {
  open: boolean;
  compagnieInitial: TCompanie;
  onCancel?: () => void;
  onClose?: () => void;
};

export const ModalAddSociety: FC<props> = ({
  open,
  compagnieInitial,
  onCancel,
  onClose,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const initCompanie = () => {
    setLocalPreview("");
    setCompanie({
      id: -1,
      name: "",
      registration_id: "",
      logo: "",
      is_fleet: 0,
      is_rental: 0,
    });
  };

  useEffect(() => {
    if (!open) {
      initCompanie();
    } else {
      setCompanie(compagnieInitial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const { mutate: createCompanie, isPending: loadingCreateCompanie } =
    useCreateCompanie(onClose, initCompanie);

  const { mutate: updateCompanie, isPending: loadingUpdateCompanie } =
    useUpdateCompanie(onClose, initCompanie);

  const [companie, setCompanie] = useState<TCompanie>(compagnieInitial);
  /** Object URL shown while the real upload is in flight. */
  const [localPreview, setLocalPreview] = useState<string>("");
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Object URLs leak until revoked. They used to be created inline in render,
  // which minted a fresh one on EVERY re-render and never released any of them.
  useEffect(() => {
    if (!localPreview) return;
    return () => URL.revokeObjectURL(localPreview);
  }, [localPreview]);

  /**
   * BUGFIX: this used to only stash the File in local state. The picked image
   * was never sent anywhere, and onSave submitted the PREVIOUS `companie.logo`
   * — so a new company always got an empty logo and editing one silently threw
   * the new image away. Upload for real, then keep the returned URL.
   */
  const onDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles?.[0];
    if (!file) return;

    setLocalPreview(URL.createObjectURL(file));
    setUploadingLogo(true);
    try {
      // "public/company-logos": persisted on the company record as a permanent
      // URL, so it must sit in the anonymously-readable prefix.
      const { url } = await uploadToStorage(file, {
        directory: "public/company-logos",
      });
      if (!url) throw new Error("no url returned");
      setCompanie((prev) => ({ ...prev, logo: url }));
    } catch (e: any) {
      console.error("[company] logo upload failed:", e);
      toast.error(
        e?.message ||
          t("compagnies.logoUploadError", "Échec du téléchargement du logo")
      );
      setLocalPreview("");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleInputChange = (fieldName: string, value: string | number) => {
    if (companie) {
      const updateCompanie = { ...companie, [fieldName]: value };
      setCompanie(updateCompanie);
    } else {
      const newCompanie: TCompanie = {
        id: -1,
        name: "",
        registration_id: "",
        logo: "",
        is_fleet: 0,
        is_rental: 0,
        [fieldName]: value,
      };
      setCompanie(newCompanie);
    }
  };

  const onSave = async () => {
    if (!companie.name?.trim().length) {
      toast.error(t("compagnies.requiredName", "Name is required"));
      return;
    }
    if (!companie.registration_id?.trim().length) {
      toast.error(t("compagnies.requiredSiret", "Siret is required"));
      return;
    }
    if (uploadingLogo) {
      toast.error(
        t("compagnies.logoStillUploading", "Logo upload still in progress")
      );
      return;
    }

    const companieData: any = {
      logo: companie.logo || "",
      name: companie.name,
      registration_id: companie.registration_id,
      is_fleet: companie.is_fleet,
      is_rental: companie.is_rental,
    };

    if (companie?.id > 0) {
      companieData.id = companie.id;
      updateCompanie(companieData);
    } else {
      createCompanie(companieData);
    }
  };

  return (
    <CustomModal
      open={open}
      onCancel={onCancel}
      title={
        compagnieInitial?.id > -1
          ? t("compagnies.modify", "Modification of a company")
          : t("compagnies.add", "Adding a new company")
      }
      onClose={onClose}
      onSave={onSave}
      loadingSave={loadingCreateCompanie || loadingUpdateCompanie || uploadingLogo}
    >
      <div className={classes.containerText}>
        <div className={classes.divImage}>
          <CustomDropZoneUpload
            onDrop={onDrop}
            imageUrl={localPreview || companie?.logo || ""}
          />
        </div>
        <CustomTextInput
          label={`${t("common.name", "Name")} *`}
          placeholder={`${t("common.name", "Name")} *`}
          marginBottom={"0px"}
          value={companie?.name}
          onChange={(e) => handleInputChange("name", e.target.value)}
        />
        <CustomTextInput
          label={`Siret *`}
          placeholder={`Siret *`}
          marginBottom={"0px"}
          value={companie?.registration_id}
          onChange={(e) => handleInputChange("registration_id", e.target.value)}
        />
        <div className={classes.checkboxDiv}>
          <CustomCheckboxSquare
            text="Fleet"
            defaultChecked={compagnieInitial?.is_fleet === 1}
            onChange={(checked) =>
              handleInputChange("is_fleet", checked ? 1 : 0)
            }
          />
          <CustomCheckboxSquare
            defaultChecked={compagnieInitial?.is_rental === 1}
            text="Rental"
            onChange={(checked) =>
              handleInputChange("is_rental", checked ? 1 : 0)
            }
          />
        </div>
      </div>
    </CustomModal>
  );
};
